import React, {PropsWithChildren} from "react";
import ReactMarkdown from "react-markdown";
import directive from "remark-directive";
import {SignTokenType} from "@data-force/app";
import htmlDirectives from "../../utils/remarkMedia";
import MarkdownAudio from "./MarkdownAudio";
import MarkdownVideo from "./MarkdownVideo";
import MarkdownImage from "./MarkdownImage";
import {MediaProps} from "./types";
import {AUDIO_EXTENSION_REGEX, PUBLIC_S3_BUCKET, VIDEO_EXTENSION_REGEX} from "../../utils/constants";

interface Props {
    children: string
    signType: SignTokenType
}

/**
 * Rewrite source direction to be able to have proper public/signable resources distinction
 * @param {string} markdown
 * @returns {string}
 */
const rewriteDirectiveSrc = (markdown: string): string => markdown.replace(
    /::(audio|video)\[(.*?)\]\{(.*?)\}/g,
    (match, tag, altText, attrs): string => {
        // Rewrite src attribute
        const newAttrs = attrs.replace(
            /src=["'].*?\/static-resources\//,
            "src=\"/static-resources/",
        );
        return `::${tag}[${altText}]{${newAttrs}}`;
    },
);

const MarkdownMedia: React.FC<Props> = ({children, signType}: Props): React.ReactElement => {
    const components = {
        audio: (
            props: PropsWithChildren<any>,
        ): React.ReactElement => <MarkdownAudio {...{...props, ...{signType}}}/>,
        video: (
            props: PropsWithChildren<any>,
        ): React.ReactElement => <MarkdownVideo {...{...props, ...{signType}}}/>,
        img: (
            props: PropsWithChildren<any>,
        ): React.ReactElement => {
            let component: React.ReactElement;
            // eslint-disable-next-line react/prop-types
            const {src}: MediaProps = props;
            // eslint-disable-next-line react/prop-types
            const isLegacySign: boolean = (src as string).includes(".") && !PUBLIC_S3_BUCKET.test(src);
            if (VIDEO_EXTENSION_REGEX.test(src)) { // legacy video
                component = <MarkdownVideo {...{...props, ...{signType, isLegacySign}}}/>;
            } else if (AUDIO_EXTENSION_REGEX.test(src)) { // legacy audio
                component = <MarkdownAudio {...{...props, ...{signType, isLegacySign}}}/>;
            } else { // (legacy or new public, or new sign)
                component = <MarkdownImage {...{...props, ...{signType, isLegacySign}}}/>;
            }
            return component;
        },
    };

    return (
        <ReactMarkdown
            linkTarget="_blank"
            remarkPlugins={[directive, htmlDirectives]}
            components={components}
        >
            {rewriteDirectiveSrc(children)}
        </ReactMarkdown>
    );
};

export default MarkdownMedia;
